export interface CertificationAmountsInput {
  workPeriod: unknown;
  advances: unknown;
  vatRatePercent: unknown;
  withholdingRatePercent?: unknown;
  withholdingAmount?: unknown;
  previousCertified: unknown;
  awardAmount: unknown;
}

export interface CertificationAmounts {
  workPeriod: string;
  advances: string;
  taxableBase: string;
  vatRatePercent: string;
  vatAmount: string;
  withholdingAmount: string;
  totalBeforeWithholding: string;
  payable: string;
  previousCertified: string;
  cumulativeCertified: string;
  awardAmount: string;
  pendingToCertify: string;
}

/** Parse canonical decimals and Spanish-formatted values without accepting ambiguous grouping. */
export function parseCertificationCents(value: unknown, allowNegative = false): number {
  if (typeof value === "number" && !Number.isFinite(value)) throw new Error("CERT_MONEY_INVALID");
  const raw = String(value ?? "").trim().replace(/[\u00a0\s€]/g, "");
  if (!raw) throw new Error("CERT_MONEY_REQUIRED");
  const negative = raw.startsWith("-");
  const unsigned = raw.replace(/^[+-]/, "");
  if (negative && !allowNegative) throw new Error("CERT_MONEY_NEGATIVE");
  let normalized: string;
  if (unsigned.includes(",")) {
    if (!/^\d{1,3}(?:\.\d{3})*(?:,\d{1,2})?$|^\d+(?:,\d{1,2})?$/.test(unsigned)) throw new Error("CERT_MONEY_INVALID");
    normalized = unsigned.replace(/\./g, "").replace(",", ".");
  } else {
    if (!/^\d+(?:\.\d{1,2})?$/.test(unsigned)) throw new Error("CERT_MONEY_INVALID");
    normalized = unsigned;
  }
  const [eurosText, centsText = ""] = normalized.split(".");
  const cents = BigInt(eurosText) * BigInt(100) + BigInt(centsText.padEnd(2, "0"));
  if (cents > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error("CERT_MONEY_TOO_LARGE");
  return Number(cents) * (negative ? -1 : 1);
}

export function formatCertificationCents(cents: number): string {
  if (!Number.isSafeInteger(cents)) throw new Error("CERT_MONEY_INVALID");
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
}

function percentBasisPoints(value: unknown): bigint {
  const raw = String(value ?? "").trim().replace("%", "").replace(",", ".");
  if (!/^\d+(?:\.\d{1,2})?$/.test(raw)) throw new Error("CERT_RATE_INVALID");
  const [whole, fraction = ""] = raw.split(".");
  const bps = BigInt(whole) * BigInt(100) + BigInt(fraction.padEnd(2, "0"));
  if (bps > BigInt(10000)) throw new Error("CERT_RATE_INVALID");
  return bps;
}

function roundedPercent(cents: number, basisPoints: bigint): number {
  const n = BigInt(cents) * basisPoints;
  const rounded = (n + BigInt(5000)) / BigInt(10000);
  if (rounded > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error("CERT_MONEY_TOO_LARGE");
  return Number(rounded);
}

export function calculateCertificationAmounts(input: CertificationAmountsInput): CertificationAmounts {
  const work = parseCertificationCents(input.workPeriod);
  const advances = parseCertificationCents(input.advances, true);
  const base = work + advances;
  if (!Number.isSafeInteger(base) || base < 0) throw new Error("CERT_BASE_INVALID");
  const vatRate = percentBasisPoints(input.vatRatePercent);
  const vat = roundedPercent(base, vatRate);
  const withholding = input.withholdingAmount !== undefined && input.withholdingAmount !== null && String(input.withholdingAmount).trim() !== ""
    ? parseCertificationCents(input.withholdingAmount)
    : roundedPercent(base, percentBasisPoints(input.withholdingRatePercent ?? "0"));
  if (withholding > base + vat) throw new Error("CERT_WITHHOLDING_EXCEEDS_TOTAL");
  const previous = parseCertificationCents(input.previousCertified);
  const award = parseCertificationCents(input.awardAmount);
  const cumulative = previous + base;
  const pending = award - cumulative;
  for (const amount of [base, vat, base + vat, base + vat - withholding, cumulative, pending]) {
    if (!Number.isSafeInteger(amount)) throw new Error("CERT_MONEY_TOO_LARGE");
  }
  return {
    workPeriod: formatCertificationCents(work),
    advances: formatCertificationCents(advances),
    taxableBase: formatCertificationCents(base),
    vatRatePercent: formatCertificationCents(Number(vatRate)),
    vatAmount: formatCertificationCents(vat),
    withholdingAmount: formatCertificationCents(withholding),
    totalBeforeWithholding: formatCertificationCents(base + vat),
    payable: formatCertificationCents(base + vat - withholding),
    previousCertified: formatCertificationCents(previous),
    cumulativeCertified: formatCertificationCents(cumulative),
    awardAmount: formatCertificationCents(award),
    pendingToCertify: formatCertificationCents(pending),
  };
}


/** Validate, without repairing, a persisted/API V1 snapshot against its source inputs. */
export function validateCertificationRevisionPayload(value: unknown): CertificationAmounts {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("CERT_REVISION_PAYLOAD_INVALID");
  const payload = value as Record<string, unknown>;
  const required = ["workPeriod", "advances", "vatRatePercent", "vatAmount", "withholdingAmount", "taxableBase", "totalBeforeWithholding", "payable", "previousCertified", "cumulativeCertified", "awardAmount", "pendingToCertify"];
  if (required.some((key) => payload[key] == null || String(payload[key]).trim() === "")) throw new Error("CERT_REVISION_PAYLOAD_INCOMPLETE");
  try {
    const expected = calculateCertificationAmounts({ workPeriod: payload.workPeriod, advances: payload.advances, vatRatePercent: payload.vatRatePercent, withholdingAmount: payload.withholdingAmount, previousCertified: payload.previousCertified, awardAmount: payload.awardAmount });
    if (parseCertificationCents(expected.taxableBase) <= 0) throw new Error("CERT_BASE_INVALID");
    for (const key of ["workPeriod", "advances", "taxableBase", "vatAmount", "withholdingAmount", "totalBeforeWithholding", "payable", "previousCertified", "cumulativeCertified", "awardAmount", "pendingToCertify"]) {
      const allowNegative = key === "advances" || key === "pendingToCertify";
      if (parseCertificationCents(payload[key], allowNegative) !== parseCertificationCents(expected[key as keyof CertificationAmounts], allowNegative)) throw new Error("CERT_REVISION_PAYLOAD_INCONSISTENT");
    }
    if (percentBasisPoints(payload.vatRatePercent) !== percentBasisPoints(expected.vatRatePercent)) throw new Error("CERT_REVISION_PAYLOAD_INCONSISTENT");
    return expected;
  } catch (error: any) {
    if (String(error?.message ?? "").startsWith("CERT_REVISION_PAYLOAD_")) throw error;
    throw new Error("CERT_REVISION_PAYLOAD_INVALID");
  }
}

const UNITS = ["cero", "uno", "dos", "tres", "cuatro", "cinco", "seis", "siete", "ocho", "nueve", "diez", "once", "doce", "trece", "catorce", "quince", "dieciséis", "diecisiete", "dieciocho", "diecinueve", "veinte"];
const TENS: Record<number, string> = { 30: "treinta", 40: "cuarenta", 50: "cincuenta", 60: "sesenta", 70: "setenta", 80: "ochenta", 90: "noventa" };
const HUNDREDS = ["", "ciento", "doscientos", "trescientos", "cuatrocientos", "quinientos", "seiscientos", "setecientos", "ochocientos", "novecientos"];

function wordsUnderThousand(n: number): string {
  if (n <= 20) return UNITS[n]!;
  if (n < 30) return ({ 21: "veintiuno", 22: "veintidós", 23: "veintitrés", 26: "veintiséis" } as Record<number, string>)[n] ?? `veinti${UNITS[n - 20]}`;
  if (n < 100) return `${TENS[Math.floor(n / 10) * 10]}${n % 10 ? ` y ${UNITS[n % 10]}` : ""}`;
  if (n === 100) return "cien";
  return `${HUNDREDS[Math.floor(n / 100)]}${n % 100 ? ` ${wordsUnderThousand(n % 100)}` : ""}`;
}

function wordsInteger(n: number): string {
  if (n < 1000) return wordsUnderThousand(n);
  if (n < 1_000_000) {
    const thousands = Math.floor(n / 1000);
    return `${thousands === 1 ? "mil" : `${wordsUnderThousand(thousands)} mil`}${n % 1000 ? ` ${wordsUnderThousand(n % 1000)}` : ""}`;
  }
  if (n < 1_000_000_000) {
    const millions = Math.floor(n / 1_000_000);
    return `${millions === 1 ? "un millón" : `${wordsInteger(millions)} millones`}${n % 1_000_000 ? ` ${wordsInteger(n % 1_000_000)}` : ""}`;
  }
  throw new Error("CERT_AMOUNT_IN_WORDS_TOO_LARGE");
}

export function certificationAmountInWords(value: unknown): string {
  const cents = parseCertificationCents(value);
  const euros = Math.floor(cents / 100);
  const centimos = cents % 100;
  const eurosWords = wordsInteger(euros)
    .replace(/veintiuno$/, "veintiún")
    .replace(/ uno$/, " un");
  return `${eurosWords} ${euros === 1 ? "euro" : "euros"} con ${wordsUnderThousand(centimos)} ${centimos === 1 ? "céntimo" : "céntimos"}`;
}
